import type { ScoredTide, TidesResponse } from "../../shared/types.js";

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

type SortKey = "date" | "time" | "height" | "score";
type SortDirection = "asc" | "desc";
interface SortState { column: SortKey; direction: SortDirection }

const DEFAULT_DIRECTION: Record<SortKey, SortDirection> = {
  date: "asc",
  time: "asc",
  height: "asc",
  score: "desc",
};

let sortState: SortState = { column: "date", direction: "asc" };
let lastData: TidesResponse | null = null;
let lastContainer: HTMLElement | null = null;

function formatDate(iso: string): string {
  const d = parseLocal(iso);
  return `${WEEKDAY[d.weekday]} ${MONTH[d.month]} ${String(d.day).padStart(2, "0")}`;
}

function formatTime(iso: string): string {
  const d = parseLocal(iso);
  const h12 = ((d.hour + 11) % 12) + 1;
  const am = d.hour < 12 ? "AM" : "PM";
  return `${h12}:${String(d.minute).padStart(2, "0")} ${am}`;
}

function parseLocal(iso: string): {
  year: number; month: number; day: number;
  hour: number; minute: number; weekday: number;
} {
  const [datePart, timePart] = iso.split("T");
  if (!datePart || !timePart) throw new Error(`Bad time: ${iso}`);
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  const utc = new Date(Date.UTC(y!, mo! - 1, d!, h!, mi!));
  return {
    year: y!, month: mo! - 1, day: d!,
    hour: h!, minute: mi!,
    weekday: utc.getUTCDay(),
  };
}

function compareTides(a: ScoredTide, b: ScoredTide, column: SortKey): number {
  switch (column) {
    case "date": return a.time.localeCompare(b.time);
    case "time": {
      const ta = a.time.split("T")[1] ?? "";
      const tb = b.time.split("T")[1] ?? "";
      return ta.localeCompare(tb);
    }
    case "height": return a.height - b.height;
    case "score": return a.score - b.score;
  }
}

function applySort(scored: ScoredTide[], state: SortState): ScoredTide[] {
  const dir = state.direction === "asc" ? 1 : -1;
  return [...scored].sort((a, b) => dir * compareTides(a, b, state.column));
}

function handleSortClick(column: SortKey): void {
  if (sortState.column === column) {
    sortState = {
      column,
      direction: sortState.direction === "asc" ? "desc" : "asc",
    };
  } else {
    sortState = { column, direction: DEFAULT_DIRECTION[column] };
  }
  if (lastData && lastContainer) renderTideTable(lastContainer, lastData);
}

function makeSortableHeader(label: string, column: SortKey): HTMLTableCellElement {
  const th = document.createElement("th");
  th.scope = "col";
  th.classList.add("sortable");

  const isActive = sortState.column === column;
  th.setAttribute(
    "aria-sort",
    isActive ? (sortState.direction === "asc" ? "ascending" : "descending") : "none"
  );

  const button = document.createElement("button");
  button.type = "button";
  button.className = "sort-button";
  button.textContent = label;

  const indicator = document.createElement("span");
  indicator.className = "sort-indicator";
  indicator.setAttribute("aria-hidden", "true");
  indicator.textContent = isActive ? (sortState.direction === "asc" ? "▲" : "▼") : "";
  button.appendChild(indicator);

  button.addEventListener("click", () => handleSortClick(column));
  th.appendChild(button);
  return th;
}

function makeStaticHeader(label: string): HTMLTableCellElement {
  const th = document.createElement("th");
  th.scope = "col";
  th.textContent = label;
  return th;
}

export function renderTideTable(
  container: HTMLElement,
  data: TidesResponse
): void {
  lastData = data;
  lastContainer = container;

  const { scored, location } = data;
  container.replaceChildren();

  if (scored.length === 0) {
    const p = document.createElement("p");
    p.className = "status";
    p.textContent = `No suitable low tides found at ${location.name} in the chosen window.`;
    container.appendChild(p);
    return;
  }

  const sorted = applySort(scored, sortState);

  const table = document.createElement("table");
  const caption = document.createElement("caption");
  caption.textContent = `Daylight low tides at ${location.name} — ${scored.length} ${scored.length === 1 ? "result" : "results"}`;
  table.appendChild(caption);

  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");
  headRow.appendChild(makeStaticHeader("#"));
  headRow.appendChild(makeSortableHeader("Date", "date"));
  headRow.appendChild(makeSortableHeader("Time", "time"));
  headRow.appendChild(makeSortableHeader("Height", "height"));
  headRow.appendChild(makeSortableHeader("Score", "score"));
  headRow.appendChild(makeStaticHeader("Notes"));
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = document.createElement("tbody");
  sorted.forEach((tide, i) => {
    tbody.appendChild(renderRow(tide, i + 1, location.id));
  });
  table.appendChild(tbody);

  container.appendChild(table);
}

function makeTag(text: string, variant: string): HTMLSpanElement {
  const span = document.createElement("span");
  span.className = `tag ${variant}`;
  span.textContent = text;
  return span;
}

function renderRow(tide: ScoredTide, rank: number, locationId: string): HTMLTableRowElement {
  const tr = document.createElement("tr");
  if (tide.height < 0) tr.className = "row-negative";

  const heightCell = document.createElement("td");
  const heightStr = `${tide.height >= 0 ? "+" : ""}${tide.height.toFixed(1)} ft`;
  heightCell.textContent = heightStr;
  if (tide.height < 0) heightCell.className = "height-negative";

  const scoreCell = document.createElement("td");
  scoreCell.className = "score-cell";
  scoreCell.textContent = tide.score.toFixed(0);
  const pct = Math.max(0, Math.min(1, (tide.score - 20) / 60));
  scoreCell.style.setProperty("--score-pct", pct.toFixed(2));

  const notesCell = document.createElement("td");
  if (tide.height <= -0.5) {
    notesCell.appendChild(makeTag("Very low", "tag-very-low"));
  } else if (tide.height <= 0) {
    notesCell.appendChild(makeTag("Negative tide", "tag-negative"));
  }
  if (tide.isWeekend) {
    notesCell.appendChild(makeTag("Weekend", "tag-weekend"));
  }

  const rankCell = document.createElement("td");
  rankCell.textContent = String(rank);
  tr.appendChild(rankCell);

  const dateCell = document.createElement("td");
  const dateStr = tide.time.slice(0, 10);
  const dateLink = document.createElement("a");
  dateLink.className = "date-link";
  dateLink.href = `/day/${encodeURIComponent(locationId)}/${dateStr}`;
  dateLink.textContent = formatDate(tide.time);
  dateCell.appendChild(dateLink);
  tr.appendChild(dateCell);

  const timeCell = document.createElement("td");
  timeCell.textContent = formatTime(tide.time);
  tr.appendChild(timeCell);

  tr.appendChild(heightCell);
  tr.appendChild(scoreCell);
  tr.appendChild(notesCell);

  return tr;
}
